import { Avatar, Box, Button, Dialog, Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { GroupListing, UserInfo } from "../../../../types";
import { useNavigate } from "react-router-dom";
import { colors } from "../../../../theme/palette";
import { useDataFormatter } from "../../../../hooks/useDataFormatter";
import { useProfileData } from "../../../../hooks/useProfileData";
import { getGroupRoute } from "../../../../routers/routeUtils";

const GroupPreviewPopUp = ({
  open,
  handleClose,
  group,
}: {
  open: boolean;
  handleClose: () => void;
  group: GroupListing | null;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!group) return <></>;
  const {
    id,
    name: groupName,
    description: groupDescription,
    imageLink: groupMedia,
    users,
  } = group;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "16px",
          width: "485px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Typography
        variant="body14Bold"
        sx={{
          display: "block",
          width: "100%",
          padding: "20px 0",
          textAlign: "center",
          borderBottom: "1px solid #EAECF0",
          marginBottom: "20px",
        }}
      >
        {t("pages.entity.groups.groupDetails")}
      </Typography>
      <Box
        sx={{
          padding: "0 30px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            rowGap: "17px",
            marginBottom: "14px",
          }}
        >
          {groupMedia ? (
            <Avatar
              src={groupMedia}
              sx={{
                width: "76px",
                height: "76px",
                marginBottom: "16px",
              }}
            />
          ) : (
            <Box
              component="span"
              sx={{
                width: "76px",
                height: "76px",
                background: colors.Gray50,
                marginBottom: "16px",
                borderRadius: "50%",
              }}
            />
          )}

          {groupName && (
            <Typography
              variant="body13Bold"
              sx={{ marginBottom: "20px", textAlign: "center" }}
            >
              {groupName}
            </Typography>
          )}
          {groupDescription && (
            <Typography
              variant="body13Regular"
              sx={{
                marginBottom: "20px",
                textAlign: "center",
                wordBreak: "break-word",
                hyphens: "auto",
                maxWidth: "100%",
              }}
            >
              {groupDescription}
            </Typography>
          )}
          <Button
            fullWidth
            variant="secondary-sm"
            onClick={() => navigate(getGroupRoute(id))}
          >
            {t("pages.entity.groups.seeDetails")}
          </Button>
        </Box>
        <Typography
          variant="body13Bold"
          sx={{ marginBottom: "20px", display: "block" }}
        >
          {`${t("pages.entity.groups.groupMembers")} (${users.length})`}
        </Typography>
        {users.length === 0 && (
          <Typography
            variant="body12Regular"
            sx={{ maxWidth: "118px", color: colors.Dark600 }}
          >
            {t("pages.entity.groups.noMembers")}
          </Typography>
        )}
        {users.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginBottom: "20px",
              maxHeight: "250px",
              overflow: "auto",
            }}
          >
            {users.map((user: UserInfo, index: number) => (
              <UserCard
                key={user.id}
                user={user}
                isLast={users.length === index + 1}
              />
            ))}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default GroupPreviewPopUp;

const UserCard = ({ user, isLast }: { user: UserInfo; isLast: boolean }) => {
  const navigate = useNavigate();
  const {
    fullName,
    firstTwoChr,
    media,
    role,
    profileLink,
    playerPositionShort,
  } = useDataFormatter(user);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        columnGap: "10px",
        paddingBottom: isLast ? "0" : "16px",
        marginBottom: isLast ? "0" : "16px",
        borderBottom: isLast ? "unset" : "1px solid #EAECF0",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-start",
          columnGap: "10px",
        }}
      >
        <Avatar
          sx={{
            width: "49px",
            height: "49px",
            position: "relative",
            overflow: "visible",
          }}
          src={media}
        >
          {firstTwoChr}
        </Avatar>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Link
            sx={{
              textDecoration: "none",
              color: colors.dark1000,
              fontSize: "16px",
              fontWeight: 700,
              lineHeight: "normal",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              cursor: "pointer",
            }}
            onClick={() => navigate(profileLink)}
          >
            {fullName}
          </Link>
          <Typography
            component="span"
            variant="body12Regular"
            sx={{
              color: colors.Dark1000,
            }}
          >
            {role}
            {playerPositionShort ? ` . (${playerPositionShort})` : ""}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
